import React from 'react';
import { Box, Snackbar, Typography } from '@mui/material';
import { useDevSettings } from '../../../contexts/DevSettingsContext';
import { PetParentAnimal } from '../../../types/PetParent';
import { useHubPetParent } from '../HubPetParentContext';
import HubSectionContainer from '../CustomContainer';
import TrainingPlan from '../Home/Checklist/TrainingPlan';
import NextUpCard from '../Home/Checklist/NextUpCard';
import TrainerContactBanner from '../Home/Checklist/TrainerContactBanner';
import { getPetAgeBracket, getRegion } from '../Home/checklistUtils';
import useChecklist from '../Home/useChecklist';
import useHubPageView from '../useHubPageView';
import { getFosterChecklist } from './checklistData';
import CompletionCard from './CompletionCard';

/**
 * /hub/home for fosters (ticket 2538): the foster 30-Day Checklist. No
 * membership upsell and no Quick Actions in any membership state.
 */
function FosterHome({ animal }: { animal: PetParentAnimal }) {
  useHubPageView();
  const { enhancePetParentData } = useDevSettings();
  const { petParent: realData, loading } = useHubPetParent();
  const petParent = enhancePetParentData(realData);

  // Kittens are type Cat, so anything that is not a dog is a cat.
  const species = animal.animalType?.toLowerCase() === 'dog' ? 'dog' : 'cat';
  const ageBracket = getPetAgeBracket(animal.birthDate);
  const checklist = React.useMemo(() => getFosterChecklist(species, ageBracket), [species, ageBracket]);
  const region = getRegion(petParent?.awo?.country);
  const expired = petParent?.membershipStatus === 'expired';

  const {
    checked, expanded, stats, done, total, percent, nextTask,
    onCheck, onExpand, goToTask, registerTaskRef, toast, clearToast,
  } = useChecklist(checklist, { petParent, loading });

  if (loading) return null;

  const orgName = petParent?.awo?.displayName ?? petParent?.awo?.name;

  return (
    <Box>
      <Box sx={{ borderBottom: '1px solid #E0E0E0' }}>
        <HubSectionContainer wide sx={{ gap: 0.5, py: 3 }}>
          <Typography variant="sectionTitle">{`Hi, ${petParent?.firstName}`}</Typography>
          <Typography variant="body2">
            {orgName
              ? `We've partnered with ${orgName} to provide you and your foster with support`
              : "We're here with resources and support for you and your foster — anytime you need us."}
          </Typography>
        </HubSectionContainer>
      </Box>
      <HubSectionContainer backgroundContrast wide sx={{ py: 3 }}>
        {nextTask
          ? <NextUpCard task={nextTask} percent={percent} done={done} total={total} onGo={goToTask} />
          : <CompletionCard orgName={orgName} />}
      </HubSectionContainer>
      {!expired && (
        <HubSectionContainer backgroundContrast wide sx={{ pb: 3.5 }}>
          <TrainerContactBanner />
        </HubSectionContainer>
      )}
      <HubSectionContainer backgroundContrast wide sx={{ pb: 4 }}>
        <TrainingPlan
          data={checklist}
          stats={stats}
          checked={checked}
          expanded={expanded}
          onCheck={onCheck}
          onExpand={onExpand}
          ageBracket={ageBracket}
          region={region}
          registerTaskRef={registerTaskRef}
        />
      </HubSectionContainer>
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={clearToast}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </Box>
  );
}

export default FosterHome;
